import { APP_NAME, SUPPORT_EMAIL } from "../app.constants";

export interface SeoPage {
  title: string;
  eyebrow: string;
  description: string;
  sections: { title: string; text: string }[];
}

export const SEO_ALTERNATIVE_PAGES: Record<string, SeoPage> = {
  "random-voice-chat": {
    eyebrow: "COMING LATER",
    title: `Random voice chat is on the roadmap`,
    description: `We are exploring voice conversations for a future ${APP_NAME} experience. Text chat is available today.`,
    sections: [
      {
        title: `Text chat is live now`,
        text: "Start an anonymous text conversation with a random person today while voice features are in development.",
      },
      {
        title: `Interest-based matching`,
        text: `Even in text mode, ${APP_NAME} finds people who share your interests for a more meaningful conversation.`,
      },
      {
        title: `Stay tuned`,
        text: "Voice chat is a future priority. Join the text community now and be ready when it arrives.",
      },
    ],
  },
  "ometv-alternative": {
    eyebrow: "RANDOM CHAT",
    title: `An OmeTV alternative focused on conversation`,
    description: `${APP_NAME} is a text-first alternative for people who want to meet someone new without opening a public profile or starting on camera.`,
    sections: [
      {
        title: `No camera required`,
        text: `Unlike OmeTV, ${APP_NAME} starts with text. You control when and whether to share more about yourself.`,
      },
      {
        title: `Interest-based matching`,
        text: "Skip the guesswork. Shared interests help you connect with people who have something in common with you from the first message.",
      },
      {
        title: "Simple safety controls",
        text: "Block, skip, or report any conversation from inside the chat. No forms, no delays.",
      },
    ],
  },
  safety: {
    eyebrow: "SAFETY FIRST",
    title: "Safety for anonymous online chat",
    description:
      "Use privacy-first habits, trust your instincts, and leave any conversation that does not feel respectful.",
    sections: [
      {
        title: "Your privacy is yours",
        text: "Never share your real name, address, or financial details with someone you just met online.",
      },
      {
        title: "Leave without hesitation",
        text: "You never owe anyone a reply. Skip, block, or close the tab the moment a conversation feels wrong.",
      },
      {
        title: "Report harmful content",
        text: "Use in-chat controls to flag harassment, threats, or content that violates community guidelines.",
      },
    ],
  },
  "random-video-chat": {
    eyebrow: "RANDOM CHAT",
    title: `Random chat with new people`,
    description: `Meet someone new through a fast, anonymous conversation. ${APP_NAME} keeps the experience simple, respectful, and focused on real connection.`,
    sections: [
      {
        title: `Fast and anonymous`,
        text: "A new match appears in seconds. No waiting rooms, no profiles to review.",
      },
      {
        title: `Interests make it better`,
        text: `Tell ${APP_NAME} what you enjoy and it will find someone who shares your vibe.`,
      },
      {
        title: `Always respectful`,
        text: "Community guidelines and in-chat moderation tools help keep every conversation comfortable.",
      },
    ],
  },
  "omegle-alternative": {
    eyebrow: "THE BEST OMEGLE ALTERNATIVE",
    title: `The best Omegle alternative — free anonymous text chat`,
    description: `Looking for an Omegle alternative? ${APP_NAME} offers free anonymous text chat with interest-based matching, no account required, and practical safety controls.`,
    sections: [
      {
        title: `Interest-first matching for better conversations`,
        text: `Unlike a purely random connection, ${APP_NAME} prioritises matching you with people who share your interests. Tell the platform what you enjoy — gaming, films, travel, languages, or anything else — and every conversation starts with something in common. You still meet someone new every time, but the first message is never a blank page.`,
      },
      {
        title: `Text-only, camera-free, and private`,
        text: `Omegle's video format put a lot of pressure on the first impression. ${APP_NAME} is built around words. There is no camera requirement, no video call, and no need to share your face before you know who you are talking to. The entire experience works through text and you control how much you reveal.`,
      },
      {
        title: `No account, no stored history, no profile`,
        text: `${APP_NAME} never asks for your email, phone number, or real name. Choose a display name for the session, start chatting, and when you leave, the conversation is gone. Messages are not stored on ${APP_NAME} servers and nothing is tied to a persistent identity. It is anonymous by design, not by accident.`,
      },
    ],
  },
  "chatroulette-alternative": {
    eyebrow: "RANDOM CONVERSATIONS",
    title: "A simple Chatroulette alternative",
    description:
      "Meet new people through a lightweight anonymous text-chat experience with interests, skip controls, and privacy-conscious matching.",
    sections: [
      {
        title: `Text without video`,
        text: `Chatroulette popularised random video matching. ${APP_NAME} takes the random-meeting idea and builds it around text instead.`,
      },
      {
        title: `Global in seconds`,
        text: "Match with someone from a different country in seconds. Interests help ensure there is always something to talk about.",
      },
      {
        title: "Full skip and block controls",
        text: "Move on whenever you want. The skip button is always one tap away, and blocks are effective immediately.",
      },
    ],
  },
};
